<template>
  <slot name="link" :clicked="onShowModal"/>
  <Transition name="fade">
    <div v-if="showModal">
    
    <!--Sticky close button-->
      <template v-if="store.isMM || store.isVRAK">
        <transition name="pop">
          <section ref="mmDialog" :id="id" class="modal" aria-modal="true" tabindex="0">
            <div class="modal__close-wrap">
              <button
                  class="modal__close"
                  @click.prevent="onCloseModal"
                  @keydown.enter="onCloseModal"
                  @keydown.space="onCloseModal"
                  tabindex="0"
                  :aria-label="store.translations.close"
              >
              </button>
            </div>
            <slot name="content" :close="onCloseModal"/>
          </section>
        </transition>
      </template>

      <!--Not sticky close button-->
      <template v-if="store.isJVM">
        <section ref="dialog" :id="id" class="modal" aria-modal="true">          
          <button 
              class="modal__close icon icon--close" 
              @click.prevent="onCloseModal" 
              :aria-label="store.translations.close">
            {{ store.translations.close }}
          </button>
          <slot name="content" :close="onCloseModal"/>
        </section>
      </template>
      <div class="modal-backdrop" @click="onCloseModal"/>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onUpdated, onMounted, watch, nextTick } from 'vue';
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";
import { useFocusTrap } from "@features/Pages/TimelineMapPage/_js/app/composables/useFocusTrap";

const store = useTimelineMapStore();

const props = defineProps({
  // If we don't want a link to trigger the showing of the modal
  show: Boolean,
  id: String,
});

const emit = defineEmits<{
  (e: 'closeModal'): void,
  (e: 'showModal'): void,
}>()

const dialog = ref<HTMLDialogElement>();
const mmDialog = ref<HTMLElement | null>(null);
const { enable: trapOn, disable: trapOff } = useFocusTrap(mmDialog, {
  includeRoot: true,
  initialFocus: () => mmDialog.value,
  onEscape: () => onCloseModal(),
  returnFocus: true,
});

const showModal = ref(false);

watch(() => store.showModal, () => {
  if (store.showModal) {
    onShowModal();
  } else {
    onCloseModal();
  }
});

const onShowModal = async () => { 
  showModal.value = true;

  if (store.isVRAK) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--vrak-scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add('disable-scroll-vrak');
  } else {
    document.body.classList.add('disable-scroll');
  }
  
  emit('showModal'); // In case someone wants to listen ¯\_(ツ)_/¯
  
  if (store.isMM || store.isVRAK) {
    await nextTick();
    trapOn();
  }
};

const onCloseModal = () => {
  showModal.value = false;

  if (store.isVRAK) {
    document.body.classList.remove('disable-scroll-vrak');
    document.body.style.removeProperty('--vrak-scrollbar-width');
  } else {
    document.body.classList.remove('disable-scroll');
  }
  window.removeEventListener('resize', onResize);

  emit('closeModal'); // In case someone wants to listen ¯\_(ツ)_/¯
  if (store.isMM || store.isVRAK) { trapOff(); }
}

onMounted(() => {
  if (props.show) {
    onShowModal();
  }
  if (store.isJVM) {
    closeModalOnBackdropClickListener();
  }
});

onUpdated(() => {
  // Delay so modal is completely rendered and we'll get an accurate height
  setTimeout(() => {
    setScrollStyle();
  }, 300);
});

// Listens to clicks outside the modal, and closes it
const closeModalOnBackdropClickListener = () => {
  dialog.value?.addEventListener('click', ((event) => {
    let rect = (event?.target as HTMLElement)?.getBoundingClientRect();

    if (rect.left > event.clientX ||
      rect.right < event.clientX ||
      rect.top > event.clientY ||
      rect.bottom < event.clientY) {
      onCloseModal();
    }
  }));
}

function onResize() {
  setTimeout(() => {
    setScrollStyle();
  }, 200);
}

// Checks if dialog is taller than the screen, and if so, adds a class to it that enables vertical scroll
function setScrollStyle() {
  if (!dialog.value) {
    return;
  }

  const children = dialog.value.children;
  let modalHeight = 0;

  if (children instanceof HTMLCollection) {
    modalHeight = Array.from(children).reduce((sum, child) => {
      return sum + (child as HTMLLIElement).offsetHeight
    }, 0);
  } else {
    modalHeight = dialog.value.offsetHeight;
  }

  if (modalHeight > window.innerHeight) {
    // Modal is taller than window, add scroll
    dialog.value.classList.add('modal--scroll');
  } else {
    dialog.value.classList.remove('modal--scroll');
  }
}
</script>
